import React, { useEffect, useRef, useState } from 'react';

import { motion } from 'framer-motion';

import { useMediaQuery } from '../../hooks/useMediaQuery';
import { FILLS, FONTS, RADII, STROKES } from '../../styles/tokens';
import { MD_BREAKPOINT, SM_BREAKPOINT } from '../../utils/breakpoints';
import { SheetFooter } from '../common/Sheet';
import { CheckCircleIcon, EmailIcon, RefreshIcon } from '../icons';

export const RESEND_CODE_COOLDOWN_SECONDS = 300;

const SECONDS_PER_MINUTE = 60;

/** Seconds as m:ss, e.g. 299 -> "4:59". */
const formatCountdown = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / SECONDS_PER_MINUTE);
  const seconds = totalSeconds % SECONDS_PER_MINUTE;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

interface EmailVerificationProps {
  email: string;
  onVerificationSuccess?: () => void;
  onResendCode: () => Promise<void>;
  onVerifyCode: (code: string) => Promise<void>;
  loading?: boolean;
  error?: string;
  success?: boolean;
  /** Seconds Resend stays disabled from open, and again whenever this changes to a positive value; 0 leaves it live. */
  resendCooldownSeconds?: number;
  /**
   * True (the pages) centres the step in a viewport-high, padded, bordered card. The pop-up
   * turns it off: the dialog already supplies the padding and the step sits at its natural height.
   */
  fillViewport?: boolean;
  /**
   * Sign-in pop-up only: the Verify Email and Continue to Login buttons render in the
   * enclosing sheet's pinned footer instead of inline. Unset keeps them inline, as the page does.
   */
  pinPrimaryToSheetFooter?: boolean;
}

const EmailVerification: React.FC<EmailVerificationProps> = ({
  email,
  onVerificationSuccess,
  onResendCode,
  onVerifyCode,
  loading = false,
  error,
  success = false,
  resendCooldownSeconds = 0,
  fillViewport = true,
  pinPrimaryToSheetFooter = false,
}) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(resendCooldownSeconds > 0);
  const [countdown, setCountdown] = useState(resendCooldownSeconds);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const isMobile = useMediaQuery(`(max-width: ${SM_BREAKPOINT - 1}px)`);
  const isTabletOrSmaller = useMediaQuery(`(max-width: ${MD_BREAKPOINT - 1}px)`);

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setResendDisabled(false);
    }
  }, [countdown]);

  useEffect(() => {
    if (resendCooldownSeconds > 0) {
      setCountdown(resendCooldownSeconds);
      setResendDisabled(true);
    }
  }, [resendCooldownSeconds]);

  const handleInputChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (newCode.every((digit) => digit !== '') && newCode.join('').length === 6) {
      handleVerify(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '');

    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setCode(newCode);
      handleVerify(pastedData);
    }
  };

  const handleVerify = async (verificationCode: string) => {
    try {
      await onVerifyCode(verificationCode);
      if (success) {
        onVerificationSuccess?.();
      }
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      await onResendCode();
      setCountdown(RESEND_CODE_COOLDOWN_SECONDS);
      setResendDisabled(true);
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setResendLoading(false);
    }
  };

  const maskEmail = (email: string) => {
    const [username, domain] = email.split('@');
    const maskedUsername =
      username.length > 2
        ? username[0] + '*'.repeat(username.length - 2) + username[username.length - 1]
        : username;
    return `${maskedUsername}@${domain}`;
  };

  const verifyButton = (
    <button
      className="btn btn-gold"
      onClick={() => handleVerify(code.join(''))}
      disabled={loading || code.some((digit) => digit === '')}
      style={{
        width: '100%',
        padding: '12px',
        fontWeight: 'bold',
        fontSize: '1rem',
        borderRadius: RADII.card,
      }}
    >
      {loading ? (
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          <span className="spinner" style={{ width: '20px', height: '20px' }} />
          Verifying...
        </span>
      ) : (
        'Verify Email'
      )}
    </button>
  );

  const continueButton = (
    <button
      className="btn"
      onClick={onVerificationSuccess}
      style={{
        width: '100%',
        padding: '12px',
        backgroundColor: FILLS.success,
        color: FILLS.textPrimary,
        fontWeight: 'bold',
        fontSize: '1rem',
        borderRadius: RADII.card,
        border: 'none',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = FILLS.successAlt;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = FILLS.success;
      }}
    >
      Continue to Login
    </button>
  );

  return (
    <>
      <div
        style={{
          ...(fillViewport && { minHeight: isMobile ? '80vh' : '100vh' }),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: fillViewport ? '16px' : 0,
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <div
            style={{
              padding: !fillViewport ? 0 : isMobile ? '8px' : isTabletOrSmaller ? '32px' : '40px',
              maxWidth: '480px',
              width: '100%',
              backgroundColor: isMobile ? 'transparent' : undefined,
              backdropFilter: isTabletOrSmaller || !fillViewport ? 'none' : 'blur(20px)',
              WebkitBackdropFilter: isTabletOrSmaller || !fillViewport ? 'none' : 'blur(20px)',
              border: isTabletOrSmaller || !fillViewport ? 'none' : `1px solid ${STROKES.goldCard}`,
              borderRadius: isTabletOrSmaller || !fillViewport ? '0' : '24px',
            }}
          >
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              >
                <div
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    backgroundColor: success ? FILLS.success : FILLS.goldAlpha10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 24px',
                    border: success
                      ? `2px solid ${FILLS.success}`
                      : `2px solid ${STROKES.goldCard}`,
                  }}
                >
                  {success ? (
                    <CheckCircleIcon size={40} style={{ color: FILLS.textPrimary }} />
                  ) : (
                    <EmailIcon size={40} style={{ color: FILLS.gold }} />
                  )}
                </div>
              </motion.div>

              <h1
                style={{
                  fontFamily: FONTS.heading,
                  fontWeight: 'bold',
                  color: FILLS.gold,
                  fontSize: isMobile ? '1.5rem' : '2rem',
                  marginBottom: '8px',
                }}
              >
                {success ? 'Email Verified!' : 'Verify Your Email'}
              </h1>

              <p
                style={{
                  color: FILLS.textPrimary,
                  marginBottom: '16px',
                  fontSize: isMobile ? '0.9rem' : '1.125rem',
                  fontFamily: FONTS.body,
                }}
              >
                {success
                  ? 'Your email has been successfully verified. You can now access your account.'
                  : `We've sent a 6-digit verification code to:`}
              </p>

              {!success && (
                <span
                  className="chip"
                  style={{
                    backgroundColor: FILLS.goldAlpha10,
                    color: FILLS.gold,
                    fontWeight: 'bold',
                  }}
                >
                  {maskEmail(email)}
                </span>
              )}
            </div>

            {!success && (
              <>
                {/* Error Alert */}
                {error && (
                  <div
                    className="alert alert-error"
                    style={{
                      marginBottom: '24px',
                      backgroundColor: FILLS.errorBg,
                      borderColor: STROKES.error,
                      color: FILLS.errorText,
                    }}
                  >
                    {error}
                  </div>
                )}

                {/* Code Input */}
                <div style={{ marginBottom: '32px' }}>
                  <p
                    style={{
                      color: FILLS.textPrimary,
                      marginBottom: '16px',
                      fontSize: isMobile ? '0.9rem' : '1.125rem',
                      textAlign: 'center',
                      fontFamily: FONTS.body,
                    }}
                  >
                    Enter the 6-digit code
                  </p>

                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      justifyContent: 'center',
                      marginBottom: '24px',
                    }}
                    onPaste={handlePaste}
                  >
                    {code.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (inputRefs.current[index] = el)}
                        value={digit}
                        onChange={(e) => handleInputChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        maxLength={1}
                        disabled={loading}
                        className="form-input"
                        style={{
                          width: isMobile ? '40px' : '56px',
                          textAlign: 'center',
                          fontSize: '1.5rem',
                          fontWeight: 'bold',
                          padding: '12px 8px',
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          borderRadius: '8px',
                        }}
                      />
                    ))}
                  </div>

                  {!pinPrimaryToSheetFooter && verifyButton}
                </div>

                <div
                  className="divider"
                  style={{ margin: '24px 0', borderColor: STROKES.goldCard }}
                />

                {/* Resend Code */}
                <div style={{ textAlign: 'center' }}>
                  <p
                    style={{
                      fontSize: '0.875rem',
                      color: FILLS.textPrimary,
                      fontFamily: FONTS.body,
                      marginBottom: '8px',
                    }}
                  >
                    Didn't receive the code?
                  </p>

                  <button
                    className="btn btn-ghost"
                    onClick={handleResend}
                    disabled={resendDisabled || resendLoading}
                    style={{
                      color: resendDisabled ? FILLS.textMuted : FILLS.gold,
                      fontWeight: 'bold',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    {resendLoading ? (
                      <span className="spinner" style={{ width: '16px', height: '16px' }} />
                    ) : (
                      <RefreshIcon size={16} />
                    )}
                    {resendLoading
                      ? 'Sending...'
                      : resendDisabled
                        ? `Resend code (${formatCountdown(countdown)})`
                        : 'Resend code'}
                  </button>
                </div>
              </>
            )}

            {success && !pinPrimaryToSheetFooter && continueButton}
          </div>
        </motion.div>
      </div>
      {pinPrimaryToSheetFooter && (
        <SheetFooter>{success ? continueButton : verifyButton}</SheetFooter>
      )}
    </>
  );
};

export default EmailVerification;
